export const normalizeCategory = (value) =>
  (value || "").toString().trim().toLowerCase();

export const resolveRuleType = (coupon) => {
  if (!coupon) return "coupon";
  if (coupon.ruleType) return coupon.ruleType;
  if (coupon.buyQty || coupon.getQty) return "bogo";
  if (coupon.category) return "category";
  return "coupon";
};

export const isRuleActive = (coupon) => coupon && coupon.isActive !== false;

const isRuleNotExpired = (coupon) => {
  if (!coupon?.expiryDate) return true;

  const expiry = coupon.expiryDate?.toDate
    ? coupon.expiryDate.toDate()
    : new Date(coupon.expiryDate);

  if (Number.isNaN(expiry.getTime())) return true;

  const endOfDay = new Date(expiry);
  endOfDay.setHours(23, 59, 59, 999);
  return new Date() <= endOfDay;
};

const isRuleWithinUsageLimit = (coupon) => {
  const maxUses = Number(coupon?.maxUses || 0);
  const currentUses = Number(coupon?.currentUses || 0);
  if (!maxUses || maxUses <= 0) return true;
  return currentUses < maxUses;
};

export const isRuleAuto = (coupon) => {
  const ruleType = resolveRuleType(coupon);
  if (ruleType === "coupon") return coupon.autoApply === true;
  return coupon.autoApply !== false;
};

const isRuleEligibleNow = (coupon) =>
  isRuleActive(coupon) &&
  isRuleNotExpired(coupon) &&
  isRuleWithinUsageLimit(coupon);

export const getCategoryRules = (coupons, category) => {
  const target = normalizeCategory(category);
  return (coupons || []).filter((coupon) => {
    const ruleType = resolveRuleType(coupon);
    return (
      ruleType === "category" &&
      isRuleEligibleNow(coupon) &&
      isRuleAuto(coupon) &&
      normalizeCategory(coupon.category) === target
    );
  });
};

export const getBogoRules = (coupons, category) => {
  const target = normalizeCategory(category);
  return (coupons || []).filter((coupon) => {
    const ruleType = resolveRuleType(coupon);
    return (
      ruleType === "bogo" &&
      isRuleEligibleNow(coupon) &&
      isRuleAuto(coupon) &&
      normalizeCategory(coupon.category) === target
    );
  });
};

export const getBestCategoryDiscount = (price, coupons, category) => {
  const rules = getCategoryRules(coupons, category);
  const base = Number(price || 0);
  if (!base || rules.length === 0) {
    return { discount: 0, finalPrice: base, rule: null };
  }

  let best = { discount: 0, finalPrice: base, rule: null };
  rules.forEach((rule) => {
    const value = Number(rule.discountValue || 0);
    if (!value) return;
    const discount =
      rule.discountType === "fixed" ? value : (base * value) / 100;
    const safeDiscount = Math.max(0, Math.min(base, discount));
    if (safeDiscount > best.discount) {
      best = {
        discount: safeDiscount,
        finalPrice: Math.max(0, base - safeDiscount),
        rule,
      };
    }
  });

  return best;
};

export const calculateCategoryDiscount = (items, coupons) => {
  let total = 0;
  const breakdown = [];

  (items || []).forEach((item) => {
    const category = item.category;
    const unitPrice = Number(item.price || 0);
    const qty = Number(item.quantity || 0);
    if (!category || !unitPrice || !qty) return;

    const result = getBestCategoryDiscount(unitPrice, coupons, category);
    if (result.discount > 0) {
      const itemDiscount = result.discount * qty;
      total += itemDiscount;
      breakdown.push({
        category,
        discount: itemDiscount,
        rule: result.rule,
      });
    }
  });

  return { total, breakdown };
};

export const calculateBogoDiscount = (items, coupons) => {
  const totalsByCategory = {};
  const pricesByCategory = {};

  (items || []).forEach((item) => {
    const category = normalizeCategory(item.category);
    if (!category) return;
    const qty = Number(item.quantity || 0);
    const price = Number(item.price || 0);
    if (!qty || !price) return;

    totalsByCategory[category] = (totalsByCategory[category] || 0) + qty;
    if (!pricesByCategory[category]) pricesByCategory[category] = [];
    pricesByCategory[category].push({ price, quantity: qty });
  });

  let total = 0;
  const details = [];

  Object.keys(totalsByCategory).forEach((category) => {
    const rules = getBogoRules(coupons, category);
    if (rules.length === 0) return;

    const rule = rules[0];
    const buyQty = Number(rule.buyQty || 0);
    const getQty = Number(rule.getQty || 0);
    if (!buyQty || !getQty) return;

    const eligibleQty = totalsByCategory[category];
    const groupSize = buyQty + getQty;
    if (!groupSize) return;
    const freeQty = Math.floor(eligibleQty / groupSize) * getQty;
    if (!freeQty) return;

    const sortedPrices = (pricesByCategory[category] || [])
      .slice()
      .sort((a, b) => a.price - b.price);

    let remainingFree = freeQty;
    let discount = 0;

    sortedPrices.forEach((entry) => {
      if (remainingFree <= 0) return;
      const take = Math.min(entry.quantity, remainingFree);
      discount += take * entry.price;
      remainingFree -= take;
    });

    if (discount > 0) {
      total += discount;
      details.push({
        category,
        freeQty,
        discount,
        rule,
      });
    }
  });

  return { total, details };
};
